import axios, { AxiosError, AxiosResponse } from 'axios'
import * as Buffer from 'buffer'
import { BehaviorSubject } from 'rxjs'
import { Ref, ref } from 'vue'

import { KnockautHandler } from './knockautHandler'

export interface KnockautBackup {
  ID: string
  Name: string
  Size: number
  Timestamp: number
}

export interface SymOSAppliance {
  AppID: number
  AppRevID: number
  Name: string // "Brelag Fusion One Controller"
  Version: string
}

export interface SymOSLicense {
  Licensee: string
  LimitDemo: number
  LimitServer: string
  LimitVariables: string
  LimitWebFront: string
}

export interface SymOSUpgrade {
  Filename: string
  Name: string
  Signature: string
  Size: number
  Timestamp: number
}

export interface SymOSUpdate extends SymOSUpgrade {}

export interface SymOSState {
  Debugging: boolean
  NeedsLicense: boolean
  NeedsResize: boolean
  NeedsSetup: boolean
  NeedsUpdate: boolean
  NeedsWelcome: boolean
  Ready: boolean
  Rebooting: boolean
  Running: boolean
  Starting: boolean
  Stopping: boolean
}

export type ProgressFunction = (progress: number) => void

export class SymOSHandler {
  private knockautHandler: KnockautHandler
  private auth: string = ''
  private _restartNeeded: boolean = false
  public isPasswordSet: Ref<boolean> = ref(false)

  constructor(knockautHandler: KnockautHandler) {
    this.knockautHandler = knockautHandler
  }

  get restartNeeded(): boolean {
    return this._restartNeeded
  }

  async getApplianceList(): Promise<SymOSAppliance[]> {
    const applicances = await this.execute('getApplianceList')
    return applicances
  }

  async downloadApplianceRevision(rev: number, progressFn?: ProgressFunction) {
    await this.execute('downloadApplianceRevision', [rev])
    await this.execute('doRestore', [''])
    await this.awaitRestoreProgress(progressFn)
  }

  async getLicense(): Promise<SymOSLicense[]> {
    const licenses = await this.execute('getLicense')
    return licenses
  }

  setSymOSPassword(password: string): string {
    const auth = Buffer.Buffer.from('symos:' + password).toString('base64')
    this.auth = auth
    this.isPasswordSet.value = true
    return auth
  }

  /**
   * Checks if update is available
   */
  async isUpdateAvailable(): Promise<boolean> {
    return this.execute('isUpdateAvailable')
  }

  /**
   * Checks if upgrade is available
   */
  async isUpgradeAvailable(): Promise<boolean> {
    return this.execute('isUpgradeAvailable')
  }

  /**
   * Returns list of available upgrades
   */
  async getUpgradeList(): Promise<SymOSUpgrade[]> {
    return this.execute('getUpgradeList')
  }

  /**
   * Returns list of available upgrades
   */
  async getUpdateList(): Promise<SymOSUpdate[]> {
    return this.execute('getUpdateList')
  }

  /**
   * Start upgrade process
   * @param upgrade selected upgrade
   */
  async startUpgrade(upgrade: SymOSUpgrade, progressFn?: ProgressFunction) {
    await this.execute('doUpgrade', [])
    await this.awaitUpgradeProgress(progressFn)
  }

  /**
   * Start update process
   * @param upgrade selected update
   */
  async startUpdate(update: SymOSUpdate, progressFn?: ProgressFunction) {
    await this.execute('doUpdate', [])
    await this.awaitUpdateProgress(progressFn)
  }

  /**
   * Restarts the system
   */
  async restart() {
    await this.doStop()
    await this.doReboot()
    // At this point the connection is lost, user has to refresh
  }

  private async awaitRestoreProgress(progressFn?: ProgressFunction) {
    let progress = await this.getRestoreProgress()
    while (progress >= 0) {
      progress = await this.getRestoreProgress()
      if (progressFn) {
        progressFn(progress)
      }
      await new Promise((r) => setTimeout(r, 500))
    }
    await this.getState()
  }

  private async awaitUpgradeProgress(progressFn?: ProgressFunction) {
    let progress = await this.getUpgradeProgress()
    while (progress >= 0) {
      progress = await this.getUpgradeProgress()
      if (progressFn) {
        progressFn(progress)
      }
      await new Promise((r) => setTimeout(r, 500))
    }
  }

  private async awaitUpdateProgress(progressFn?: ProgressFunction) {
    let progress = await this.getUpdateProgress()
    while (progress >= 0) {
      progress = await this.getUpdateProgress()
      if (progressFn) {
        progressFn(progress)
      }
      await new Promise((r) => setTimeout(r, 500))
    }
  }

  private async doStop() {
    let status = await this.getState()
    await this.execute('doStop')
    while (status.Running) {
      status = await this.getState()
      // TODO: Timeout ? Retries?
    }
  }

  private async doReboot() {
    await this.execute('doReboot')
  }

  private async getState(): Promise<SymOSState> {
    return this.execute('getState')
  }

  private async getRestoreProgress(): Promise<number> {
    return this.execute('getRestoreProgress')
  }

  private async getUpgradeProgress(): Promise<number> {
    return this.execute('getUpgradeDownload')
  }

  private async getUpdateProgress(): Promise<number> {
    return this.execute('getUpdateDownload')
  }

  /**
   * Returns download link for backup
   * @param filename Filename of Backup
   */
  getDownloadLink(filename: string): string {
    const name = filename.replace('.zip', '')
    const url = `${this.backupLink}${name}?authorization=${this.auth}`
    return url
  }

  /**
   * Returns the address that should be used (either local or remote link)
   */
  get address(): string {
    return `https://${
      this.knockautHandler.project.remote_access_link + '/symos'
    }`
  }

  get backupLink(): string {
    return `${this.address}/backup/`
  }

  private async execute(method: string, params: (number | string)[] = []) {
    let response: AxiosResponse
    try {
      response = await axios.post(
        `${this.address}/api/`,
        this.buildData(method, params),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Basic ' + this.auth,
          },
          withCredentials: false,
          xsrfCookieName: 'csrftoken',
        }
      )
    } catch (error) {
      const axiosError = error as AxiosError
      // TODO: This does not really work, because the 509 is returned in the OPTIONS call. -> Use interceptor
      if (axiosError.response && error.response.status === 509) {
        throw new Error(
          'Maximale Datennutzung der FusionONE erreicht. Bitte FusionONE neu starten oder einen Tag warten.'
        )
      } else if (error.response && error.response.data) {
        response = error.response
      } else {
        throw error
      }
    }
    if (response.data.error) {
      if (response.data.error.message) {
        throw new Error(response.data.error.message)
      } else {
        throw new Error(response.data.error)
      }
    }
    return response.data.result
  }

  private buildData(method: string, params: (number | string)[] = []) {
    return {
      jsonrpc: '2.0',
      method,
      params,
      id: Date.now(),
    }
  }
}
